import React from "react";
import { Link } from "react-router-dom";
import { Search, Filter, ChevronDown, Sparkles } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { CATEGORIES } from "@/data/products";

export default function StoreFilters({
  category,
  setCategory,
  search,
  setSearch,
  availableOnly,
  setAvailableOnly,
  sortBy,
  setSortBy,
}) {
  const { t } = useLanguage();
  const S = t.store;
  const SH = t.shein;
  const orderedCategories = [
    "clothing",
    "women",
    "men",
    "kids",
    "shoes",
    "accessories",
    "perfumes",
    "cosmetics",
    "others",
  ].filter((value, index, items) => items.indexOf(value) === index && CATEGORIES.includes(value));

  return (
    <div className="space-y-5" data-testid="store-filters">
      <div className="relative">
        <Search className="absolute start-4 top-1/2 h-4 w-4 -translate-y-1/2 text-[#888] dark:text-[#8FA1B8]" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={S.searchPlaceholder}
          className="h-12 w-full rounded-full border border-black/10 bg-[#FAFAFA] pe-4 ps-11 text-sm text-[#111] placeholder:text-[#999] transition-colors focus:border-[#F37021] focus:outline-none dark:border-white/10 dark:bg-[#121212] dark:text-white"
          data-testid="store-search-input"
          aria-label={S.searchPlaceholder}
        />
      </div>

      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="-mx-1 flex gap-2 overflow-x-auto px-1 pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden lg:mx-0 lg:flex-wrap lg:overflow-visible lg:px-0">
          <CategoryPill
            active={category === "all"}
            onClick={() => setCategory("all")}
            testId="cat-all"
          >
            {S.categoryAll}
          </CategoryPill>
          <Link
            to="/stock/shein"
            className="inline-flex h-10 shrink-0 items-center gap-1.5 rounded-full bg-gradient-to-r from-[#F5B3CB] via-[#F37CAA] to-[#F37021] px-5 text-sm font-bold text-white shadow-[0_8px_22px_rgba(243,124,170,0.28)] transition-all hover:-translate-y-0.5 hover:from-[#F37CAA] hover:via-[#F5559D] hover:to-[#E06015]"
            data-testid="store-cat-shein"
          >
            <Sparkles className="h-3.5 w-3.5" strokeWidth={2.5} />
            {SH.navPillLabel}
          </Link>
          <CategoryPill
            active={category === "authentic"}
            onClick={() => setCategory("authentic")}
            testId="cat-authentic"
            highlight
          >
            {S.categories.authentic}
          </CategoryPill>
          {orderedCategories.map((c) => (
            <CategoryPill
              key={c}
              active={category === c}
              onClick={() => setCategory(c)}
              testId={`cat-${c}`}
            >
              {S.categories[c]}
            </CategoryPill>
          ))}
        </div>

        <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
          <button
            type="button"
            onClick={() => setAvailableOnly((v) => !v)}
            className={`inline-flex h-10 w-full items-center justify-center gap-2 rounded-full px-4 text-sm font-medium transition-all sm:w-auto ${
              availableOnly
                ? "border border-[#111] bg-[#111] text-white shadow-[0_12px_26px_rgba(17,17,17,0.12)]"
                : "border border-black/10 bg-[#FAFAFA] text-[#111] hover:border-[#111] hover:bg-white dark:border-white/10 dark:bg-[#121212] dark:text-white dark:hover:border-[#F8A056] dark:hover:bg-[#1B1B1B]"
            }`}
            data-testid="store-toggle-available"
          >
            <Filter className="h-4 w-4" />
            {S.availableOnly}
          </button>

          <div className="relative w-full sm:w-auto">
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="h-10 w-full cursor-pointer appearance-none rounded-full border border-black/10 bg-[#FAFAFA] pe-9 ps-4 text-sm font-medium text-[#111] transition-colors hover:border-[#111] hover:bg-white focus:border-[#F37021] focus:outline-none sm:min-w-[180px] dark:border-white/10 dark:bg-[#121212] dark:text-white dark:hover:border-[#F8A056] dark:hover:bg-[#1B1B1B]"
              data-testid="store-sort-select"
              aria-label={S.sortBy}
            >
              <option value="default">{S.sortDefault}</option>
              <option value="priceLow">{S.sortPriceLow}</option>
              <option value="priceHigh">{S.sortPriceHigh}</option>
            </select>
            <ChevronDown className="pointer-events-none absolute end-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#666] dark:text-[#8FA1B8]" />
          </div>
        </div>
      </div>
    </div>
  );
}

const CategoryPill = ({ active, onClick, children, testId, highlight }) => (
  <button
    type="button"
    onClick={onClick}
    className={`inline-flex h-10 shrink-0 items-center gap-1.5 rounded-full px-5 text-sm font-medium transition-all ${
      active
        ? "border border-[#F37021] bg-[#F37021] text-white shadow-[0_12px_26px_rgba(243,112,33,0.2)]"
        : highlight
        ? "border border-[#E2B658]/40 bg-gradient-to-r from-[#FFF7E6] to-[#FFE9C4] text-[#7A4F00] hover:from-[#FCE9B0] hover:to-[#FFD074]"
        : "border border-black/10 bg-[#FAFAFA] text-[#111] hover:border-[#F37021] hover:bg-white hover:text-[#F37021] dark:border-white/10 dark:bg-[#121212] dark:text-white dark:hover:border-[#F8A056] dark:hover:bg-[#1B1B1B] dark:hover:text-[#F8A056]"
    }`}
    data-testid={`store-${testId}`}
  >
    {children}
  </button>
);
